/**
 * 设置模块 - 应用设置与配置
 * 独立模块，与 UIController 解耦
 */

import { appInstance } from '../../main';

// 动画风格类型
export type AnimationStyle = 'off' | 'efficient' | 'comfort' | 'fresh';

// 应用主题类型
export type AppTheme = 'dark' | 'light';

// 通用设置接口
export interface GeneralSettings {
  animationStyle: AnimationStyle;  // 动画风格
  appTheme: AppTheme;              // 应用主题
}

// 默认通用设置
const DEFAULT_GENERAL_SETTINGS: GeneralSettings = {
  animationStyle: 'comfort',  // 默认舒适模式
  appTheme: 'dark'            // 默认深色主题
};

// 编辑器设置接口
export interface EditorSettings {
  theme: 'dracula' | 'monokai' | 'github-dark' | 'one-dark';
  fontSize: number;
  lineNumbers: boolean;
  autoComplete: boolean;
  lineWrapping: boolean;      // 自动换行
  styleActiveLine: boolean;   // 光标行高亮
  matchBrackets: boolean;     // 括号匹配高亮
}

// 默认编辑器设置
const DEFAULT_EDITOR_SETTINGS: EditorSettings = {
  theme: 'dracula',
  fontSize: 14,
  lineNumbers: true,
  autoComplete: true,
  lineWrapping: true,
  styleActiveLine: true,
  matchBrackets: true
};

// 渲染器设置接口
export interface RendererSettings {
  antialias: 'off' | 'fxaa' | 'msaa2x' | 'msaa4x';  // 抗锯齿级别
  shadows: boolean;        // 阴影效果
  showGrid: boolean;       // 显示网格线
  quality: 'low' | 'medium' | 'high';  // 渲染质量
  showFps: boolean;        // 帧率监控
}

// 默认渲染器设置
const DEFAULT_RENDERER_SETTINGS: RendererSettings = {
  antialias: 'msaa2x',
  shadows: true,
  showGrid: true,
  quality: 'medium',
  showFps: true
};

// 积木器设置接口
export interface BlocksSettings {
  defaultStageType: 'pen' | 'sprite' | 'text';  // 默认舞台类型
  executionSpeed: 'slow' | 'normal' | 'fast';   // 执行速度
  showGrid: boolean;                             // 显示画布网格
  showRuler: boolean;                            // 显示画布尺子
}

// 默认积木器设置
const DEFAULT_BLOCKS_SETTINGS: BlocksSettings = {
  defaultStageType: 'pen',
  executionSpeed: 'fast',
  showGrid: true,
  showRuler: true
};

export class SettingsModule {
  private currentTab: string = 'general';
  private generalSettings: GeneralSettings = { ...DEFAULT_GENERAL_SETTINGS };
  private editorSettings: EditorSettings = { ...DEFAULT_EDITOR_SETTINGS };
  private rendererSettings: RendererSettings = { ...DEFAULT_RENDERER_SETTINGS };
  private blocksSettings: BlocksSettings = { ...DEFAULT_BLOCKS_SETTINGS };
  private onEditorSettingsChange?: (settings: EditorSettings) => void;
  private onBlocksSettingsChange?: (settings: BlocksSettings) => void;
  
  constructor() {
    console.log('⚙️ 设置模块初始化');
    // 构造时就加载设置，确保编辑器初始化时能读取到正确的配置
    this.loadGeneralSettings();
    this.loadEditorSettings();
    this.loadRendererSettings();
    this.loadBlocksSettings();
    this.applyAnimationSettings();
    this.applyThemeSettings();
  }
  
  /**
   * 设置编辑器设置变更回调
   */
  setEditorSettingsChangeCallback(callback: (settings: EditorSettings) => void): void {
    this.onEditorSettingsChange = callback;
  }
  
  /**
   * 初始化模块
   */
  init(): void {
    this.loadEditorSettings();
    this.bindTabEvents();
    this.initTabIndicator();
    this.renderGeneralView();
    this.renderBlocksView();
    this.renderEditorView();
    this.renderRendererView();
    console.log('⚙️ 设置模块已挂载');
  }
  
  /**
   * 刷新模块（切换到设置页面时调用）
   */
  refresh(): void {
    // 重新初始化指示器位置
    const activeTab = document.querySelector('.settings-tab.active') as HTMLElement;
    if (activeTab) {
      this.updateTabIndicator(activeTab);
    }
  }
  
  /**
   * 绑定TAB切换事件
   */
  private bindTabEvents(): void {
    const tabs = document.querySelectorAll('.settings-tab');
    tabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const tabName = target.dataset.tab;
        if (tabName) {
          this.switchTab(tabName);
        }
      });
    });
  }
  
  /**
   * 初始化TAB滑动指示器
   */
  private initTabIndicator(): void {
    const tabsContainer = document.querySelector('.settings-tabs');
    if (!tabsContainer) return;
    
    // 创建指示器元素
    let indicator = tabsContainer.querySelector('.settings-tab-indicator') as HTMLElement;
    if (!indicator) {
      indicator = document.createElement('div');
      indicator.className = 'settings-tab-indicator';
      tabsContainer.insertBefore(indicator, tabsContainer.firstChild);
    }
    
    // 初始化位置
    const initPosition = () => {
      const activeTab = tabsContainer.querySelector('.settings-tab.active') as HTMLElement;
      if (activeTab && activeTab.getBoundingClientRect().width > 0) {
        this.updateTabIndicator(activeTab);
      }
    };
    
    initPosition();
    setTimeout(initPosition, 100);
    setTimeout(initPosition, 300);
  }
  
  /**
   * 更新TAB指示器位置
   */
  private updateTabIndicator(activeTab: HTMLElement): void {
    const tabsContainer = document.querySelector('.settings-tabs') as HTMLElement;
    const indicator = tabsContainer?.querySelector('.settings-tab-indicator') as HTMLElement;
    if (!tabsContainer || !indicator) return;
    
    const containerRect = tabsContainer.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();
    
    const left = tabRect.left - containerRect.left;
    const width = tabRect.width;
    
    indicator.style.left = `${left}px`;
    indicator.style.width = `${width}px`;
  }
  
  /**
   * 切换TAB
   */
  private switchTab(tabName: string): void {
    this.currentTab = tabName;
    
    // 更新TAB按钮状态
    const tabs = document.querySelectorAll('.settings-tab');
    tabs.forEach(tab => {
      tab.classList.toggle('active', tab.getAttribute('data-tab') === tabName);
    });
    
    // 更新指示器位置
    const activeTab = document.querySelector('.settings-tab.active') as HTMLElement;
    if (activeTab) {
      this.updateTabIndicator(activeTab);
    }
    
    // 切换视图
    const views = document.querySelectorAll('.settings-view');
    views.forEach(view => {
      view.classList.toggle('active', view.getAttribute('data-view') === tabName);
    });
    
    console.log(`⚙️ 切换到设置TAB: ${tabName}`);
  }
  
  /**
   * 渲染通用设置视图
   */
  private renderGeneralView(): void {
    const container = document.getElementById('settings-general');
    if (!container) return;
    
    const { animationStyle } = this.generalSettings;
    
    container.innerHTML = `
      <div class="settings-page">
        <div class="settings-content">
          <div class="settings-section">
            <h3 class="section-title">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z"/>
              </svg>
              通用设置
            </h3>
            <p class="section-desc">配置应用程序的整体表现和外观</p>
          </div>
          
          <div class="setting-cards">
            <!-- 动画风格 -->
            <div class="setting-item setting-item-vertical">
              <div class="setting-info">
                <span class="setting-label">动画风格</span>
                <span class="setting-desc">设置界面过渡动画的速度与效果</span>
              </div>
              <div class="animation-style-grid">
                <div class="animation-style-card ${animationStyle === 'off' ? 'active' : ''}" data-style="off">
                  <div class="style-icon">
                    <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM4 12c0-4.42 3.58-8 8-8 1.85 0 3.55.63 4.9 1.69L5.69 16.9C4.63 15.55 4 13.85 4 12zm8 8c-1.85 0-3.55-.63-4.9-1.69L18.31 7.1C19.37 8.45 20 10.15 20 12c0 4.42-3.58 8-8 8z"/>
                    </svg>
                  </div>
                  <span class="style-name">关闭</span>
                  <span class="style-desc">无动画</span>
                </div>
                <div class="animation-style-card ${animationStyle === 'efficient' ? 'active' : ''}" data-style="efficient">
                  <div class="style-icon">
                    <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                      <path d="M20.38 8.57l-1.23 1.85a8 8 0 01-.22 7.58H5.07A8 8 0 0115.58 6.85l1.85-1.23A10 10 0 003.35 19a2 2 0 001.72 1h13.85a2 2 0 001.74-1 10 10 0 00-.27-10.44z"/>
                      <path d="M10.59 15.41a2 2 0 002.83 0l5.66-8.49-8.49 5.66a2 2 0 000 2.83z"/>
                    </svg>
                  </div>
                  <span class="style-name">高效</span>
                  <span class="style-desc">极速响应</span>
                </div>
                <div class="animation-style-card ${animationStyle === 'comfort' ? 'active' : ''}" data-style="comfort">
                  <div class="style-icon">
                    <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                  </div>
                  <span class="style-name">舒适</span>
                  <span class="style-desc">平滑自然</span>
                </div>
                <div class="animation-style-card ${animationStyle === 'fresh' ? 'active' : ''}" data-style="fresh">
                  <div class="style-icon">
                    <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                      <path d="M12 3L1 9l4 2.18v6L12 21l7-3.82v-6l2-1.09V17h2V9L12 3zm6.82 6L12 12.72 5.18 9 12 5.28 18.82 9zM17 15.99l-5 2.73-5-2.73v-3.72L12 15l5-2.73v3.72z"/>
                    </svg>
                  </div>
                  <span class="style-name">清爽</span>
                  <span class="style-desc">弹性跳动</span>
                </div>
              </div>
            </div>
            
            <!-- 应用主题（暂时隐藏） -->
            <div class="setting-item" style="display: none;">
              <div class="setting-info">
                <span class="setting-label">应用主题</span>
                <span class="setting-desc">切换深色或明亮外观风格</span>
              </div>
              <div class="setting-control">
                <div class="theme-toggle-group">
                  <button class="theme-toggle-btn ${this.generalSettings.appTheme === 'dark' ? 'active' : ''}" data-theme="dark">
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                      <path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9 9-4.03 9-9c0-.46-.04-.92-.1-1.36-.98 1.37-2.58 2.26-4.4 2.26-2.98 0-5.4-2.42-5.4-5.4 0-1.81.89-3.42 2.26-4.4-.44-.06-.9-.1-1.36-.1z"/>
                    </svg>
                    深色
                  </button>
                  <button class="theme-toggle-btn ${this.generalSettings.appTheme === 'light' ? 'active' : ''}" data-theme="light">
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                      <path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zM2 13h2c.55 0 1-.45 1-1s-.45-1-1-1H2c-.55 0-1 .45-1 1s.45 1 1 1zm18 0h2c.55 0 1-.45 1-1s-.45-1-1-1h-2c-.55 0-1 .45-1 1s.45 1 1 1zM11 2v2c0 .55.45 1 1 1s1-.45 1-1V2c0-.55-.45-1-1-1s-1 .45-1 1zm0 18v2c0 .55.45 1 1 1s1-.45 1-1v-2c0-.55-.45-1-1-1s-1 .45-1 1zM5.99 4.58c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0s.39-1.03 0-1.41L5.99 4.58zm12.37 12.37c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0 .39-.39.39-1.03 0-1.41l-1.06-1.06zm1.06-10.96c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06zM7.05 18.36c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06z"/>
                    </svg>
                    明亮
                  </button>
                </div>
              </div>
            </div>
          </div>
            
          </div>
        </div>
      </div>
    `;
    
    // 绑定动画风格选择事件
    this.bindGeneralEvents();
  }
  
  /**
   * 绑定通用设置事件
   */
  private bindGeneralEvents(): void {
    // 动画风格选择
    const styleCards = document.querySelectorAll('.animation-style-card');
    styleCards.forEach(card => {
      card.addEventListener('click', () => {
        const style = card.getAttribute('data-style') as AnimationStyle;
        if (style) {
          this.generalSettings.animationStyle = style;
          // 更新选中态
          styleCards.forEach(c => c.classList.remove('active'));
          card.classList.add('active');
          this.saveGeneralSettings();
          this.applyAnimationSettings();
          
          const styleNames: Record<AnimationStyle, string> = {
            'off': '关闭',
            'efficient': '高效',
            'comfort': '舒适',
            'fresh': '清爽'
          };
          console.log(`⚙️ 动画风格: ${styleNames[style]}`);
        }
      });
    });
    
    // 应用主题切换
    const themeBtns = document.querySelectorAll('.theme-toggle-btn');
    themeBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const theme = btn.getAttribute('data-theme') as AppTheme;
        if (theme) {
          this.generalSettings.appTheme = theme;
          // 更新选中态
          themeBtns.forEach(b => b.classList.remove('active'));
          btn.classList.add('active');
          this.saveGeneralSettings();
          this.applyThemeSettings();
          
          console.log(`⚙️ 应用主题: ${theme === 'dark' ? '深色' : '明亮'}`);
        }
      });
    });
  }
  
  /**
   * 渲染编辑器设置视图
   */
  private renderEditorView(): void {
    const container = document.getElementById('settings-editor');
    if (!container) return;
    
    const { theme, fontSize, lineNumbers, autoComplete, lineWrapping, styleActiveLine, matchBrackets } = this.editorSettings;
    
    container.innerHTML = `
      <div class="settings-page">
        <div class="settings-content">
          <div class="settings-section">
            <h3 class="section-title">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                <path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"/>
              </svg>
              编辑器设置
            </h3>
            <p class="section-desc">配置代码编辑器的外观和行为</p>
          </div>
          
          <div class="setting-cards">
            <!-- 主题选择 -->
            <div class="setting-item setting-item-themes">
              <div class="setting-info">
                <span class="setting-label">编辑器主题</span>
                <span class="setting-desc">选择代码编辑器的配色主题</span>
              </div>
              <div class="theme-previews">
                <!-- 德古拉紫 (dracula) -->
                <div class="theme-preview-card ${theme === 'dracula' ? 'active' : ''}" data-theme="dracula">
                  <div class="theme-preview cm-s-dracula">
                    <div class="preview-titlebar">
                      <span class="preview-dot red"></span>
                      <span class="preview-dot yellow"></span>
                      <span class="preview-dot green"></span>
                    </div>
                    <div class="preview-code">
                      <div class="preview-line"><span class="cm-keyword">func</span> <span class="cm-def">main</span>(<span class="cm-variable-2">n</span>)</div>
                      <div class="preview-line"><span class="cm-comment">  // code</span></div>
                      <div class="preview-line">  <span class="cm-keyword">return</span> <span class="cm-number">0</span></div>
                    </div>
                  </div>
                  <span class="theme-preview-name">德古拉紫</span>
                </div>
                
                <!-- 莫诺凯棕 (monokai) -->
                <div class="theme-preview-card ${theme === 'monokai' ? 'active' : ''}" data-theme="monokai">
                  <div class="theme-preview cm-s-monokai">
                    <div class="preview-titlebar">
                      <span class="preview-dot red"></span>
                      <span class="preview-dot yellow"></span>
                      <span class="preview-dot green"></span>
                    </div>
                    <div class="preview-code">
                      <div class="preview-line"><span class="cm-keyword">func</span> <span class="cm-def">main</span>(<span class="cm-variable-2">n</span>)</div>
                      <div class="preview-line"><span class="cm-comment">  // code</span></div>
                      <div class="preview-line">  <span class="cm-keyword">return</span> <span class="cm-number">0</span></div>
                    </div>
                  </div>
                  <span class="theme-preview-name">莫诺凯棕</span>
                </div>
                
                <!-- 深空黑 (material-darker) -->
                <div class="theme-preview-card ${theme === 'github-dark' ? 'active' : ''}" data-theme="github-dark">
                  <div class="theme-preview cm-s-material-darker">
                    <div class="preview-titlebar">
                      <span class="preview-dot red"></span>
                      <span class="preview-dot yellow"></span>
                      <span class="preview-dot green"></span>
                    </div>
                    <div class="preview-code">
                      <div class="preview-line"><span class="cm-keyword">func</span> <span class="cm-def">main</span>(<span class="cm-variable-2">n</span>)</div>
                      <div class="preview-line"><span class="cm-comment">  // code</span></div>
                      <div class="preview-line">  <span class="cm-keyword">return</span> <span class="cm-number">0</span></div>
                    </div>
                  </div>
                  <span class="theme-preview-name">深空黑</span>
                </div>
                
                <!-- 曜石蓝 (material-ocean) -->
                <div class="theme-preview-card ${theme === 'one-dark' ? 'active' : ''}" data-theme="one-dark">
                  <div class="theme-preview cm-s-material-ocean">
                    <div class="preview-titlebar">
                      <span class="preview-dot red"></span>
                      <span class="preview-dot yellow"></span>
                      <span class="preview-dot green"></span>
                    </div>
                    <div class="preview-code">
                      <div class="preview-line"><span class="cm-keyword">func</span> <span class="cm-def">main</span>(<span class="cm-variable-2">n</span>)</div>
                      <div class="preview-line"><span class="cm-comment">  // code</span></div>
                      <div class="preview-line">  <span class="cm-keyword">return</span> <span class="cm-number">0</span></div>
                    </div>
                  </div>
                  <span class="theme-preview-name">曜石蓝</span>
                </div>
              </div>
            </div>
            
            <!-- 字体大小 -->
            <div class="setting-item setting-item-fontsize">
              <div class="setting-info">
                <span class="setting-label">字体大小</span>
                <span class="setting-desc">调整代码编辑器的字体大小</span>
              </div>
              <div class="fontsize-wrapper">
                <div class="fontsize-preview">
                  <div class="preview-titlebar" style="background: #21222c;">
                    <span class="preview-dot" style="background: #ff5555;"></span>
                    <span class="preview-dot" style="background: #f1fa8c;"></span>
                    <span class="preview-dot" style="background: #50fa7b;"></span>
                  </div>
                  <div class="fontsize-preview-code" id="fontsize-preview-code" style="font-size: ${fontSize}px;">
                    <div class="preview-line"><span style="color: #ff79c6;">function</span> <span style="color: #50fa7b;">hello</span><span style="color: #f8f8f2;">()</span></div>
                    <div class="preview-line"><span style="color: #f8f8f2;">  </span><span style="color: #ff79c6;">return</span> <span style="color: #f1fa8c;">"world"</span></div>
                  </div>
                </div>
                <div class="fontsize-control-wrap">
                  <div class="font-size-control">
                    <button class="font-size-btn" id="font-size-decrease">-</button>
                    <span class="font-size-value" id="font-size-display">${fontSize}px</span>
                    <button class="font-size-btn" id="font-size-increase">+</button>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- 行号显示 -->
            <div class="setting-item">
              <div class="setting-info">
                <span class="setting-label">显示行号 <span class="setting-tag">重启生效</span></span>
                <span class="setting-desc">在编辑器左侧显示代码行号</span>
              </div>
              <div class="setting-control">
                <div class="setting-switch ${lineNumbers ? 'active' : ''}" id="toggle-line-numbers">
                  <div class="switch-slider"></div>
                </div>
              </div>
            </div>
            
            <!-- 自动补全 -->
            <div class="setting-item">
              <div class="setting-info">
                <span class="setting-label">自动补全</span>
                <span class="setting-desc">开启代码自动补全功能</span>
              </div>
              <div class="setting-control">
                <div class="setting-switch ${autoComplete ? 'active' : ''}" id="toggle-auto-complete">
                  <div class="switch-slider"></div>
                </div>
              </div>
            </div>
            
            <!-- 自动换行 -->
            <div class="setting-item">
              <div class="setting-info">
                <span class="setting-label">自动换行</span>
                <span class="setting-desc">代码超出编辑器宽度时自动换行</span>
              </div>
              <div class="setting-control">
                <div class="setting-switch ${lineWrapping ? 'active' : ''}" id="toggle-line-wrapping">
                  <div class="switch-slider"></div>
                </div>
              </div>
            </div>
            
            <!-- 光标行高亮 -->
            <div class="setting-item">
              <div class="setting-info">
                <span class="setting-label">光标行高亮</span>
                <span class="setting-desc">高亮显示当前光标所在行</span>
              </div>
              <div class="setting-control">
                <div class="setting-switch ${styleActiveLine ? 'active' : ''}" id="toggle-active-line">
                  <div class="switch-slider"></div>
                </div>
              </div>
            </div>
            
            <!-- 括号匹配高亮 -->
            <div class="setting-item">
              <div class="setting-info">
                <span class="setting-label">括号匹配高亮</span>
                <span class="setting-desc">高亮显示匹配的括号对</span>
              </div>
              <div class="setting-control">
                <div class="setting-switch ${matchBrackets ? 'active' : ''}" id="toggle-match-brackets">
                  <div class="switch-slider"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
    
    // 绑定事件
    this.bindEditorSettingsEvents();
  }
  
  /**
   * 绑定编辑器设置事件
   */
  private bindEditorSettingsEvents(): void {
    // 主题选择预览卡片
    const themeCards = document.querySelectorAll('.theme-preview-card');
    themeCards.forEach(card => {
      card.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const themeValue = target.dataset.theme as EditorSettings['theme'];
        if (themeValue) {
          // 更新设置
          this.editorSettings.theme = themeValue;
          this.saveEditorSettings();
          
          // 更新UI状态
          themeCards.forEach(c => c.classList.remove('active'));
          target.classList.add('active');
          
          console.log(`⚙️ 编辑器主题已更改为: ${themeValue}`);
        }
      });
    });
    
    // 字体大小减小
    const decreaseBtn = document.getElementById('font-size-decrease');
    if (decreaseBtn) {
      decreaseBtn.addEventListener('click', () => {
        if (this.editorSettings.fontSize > 10) {
          this.editorSettings.fontSize -= 1;
          this.updateFontSizeDisplay();
          this.saveEditorSettings();
        }
      });
    }
    
    // 字体大小增大
    const increaseBtn = document.getElementById('font-size-increase');
    if (increaseBtn) {
      increaseBtn.addEventListener('click', () => {
        if (this.editorSettings.fontSize < 24) {
          this.editorSettings.fontSize += 1;
          this.updateFontSizeDisplay();
          this.saveEditorSettings();
        }
      });
    }
    
    // 行号显示开关（重启生效，不触发热更新回调）
    const lineNumbersToggle = document.getElementById('toggle-line-numbers');
    if (lineNumbersToggle) {
      lineNumbersToggle.addEventListener('click', () => {
        this.editorSettings.lineNumbers = !this.editorSettings.lineNumbers;
        lineNumbersToggle.classList.toggle('active', this.editorSettings.lineNumbers);
        this.saveEditorSettings(false);  // 不触发回调
        console.log(`⚙️ 行号显示: ${this.editorSettings.lineNumbers ? '开启' : '关闭'} (重启后生效)`);
      });
    }
    
    // 自动补全开关
    const autoCompleteToggle = document.getElementById('toggle-auto-complete');
    if (autoCompleteToggle) {
      autoCompleteToggle.addEventListener('click', () => {
        this.editorSettings.autoComplete = !this.editorSettings.autoComplete;
        autoCompleteToggle.classList.toggle('active', this.editorSettings.autoComplete);
        this.saveEditorSettings();
        console.log(`⚙️ 自动补全: ${this.editorSettings.autoComplete ? '开启' : '关闭'}`);
      });
    }
    
    // 自动换行开关
    const lineWrappingToggle = document.getElementById('toggle-line-wrapping');
    if (lineWrappingToggle) {
      lineWrappingToggle.addEventListener('click', () => {
        this.editorSettings.lineWrapping = !this.editorSettings.lineWrapping;
        lineWrappingToggle.classList.toggle('active', this.editorSettings.lineWrapping);
        this.saveEditorSettings();
        console.log(`⚙️ 自动换行: ${this.editorSettings.lineWrapping ? '开启' : '关闭'}`);
      });
    }
    
    // 光标行高亮开关
    const activeLineToggle = document.getElementById('toggle-active-line');
    if (activeLineToggle) {
      activeLineToggle.addEventListener('click', () => {
        this.editorSettings.styleActiveLine = !this.editorSettings.styleActiveLine;
        activeLineToggle.classList.toggle('active', this.editorSettings.styleActiveLine);
        this.saveEditorSettings();
        console.log(`⚙️ 光标行高亮: ${this.editorSettings.styleActiveLine ? '开启' : '关闭'}`);
      });
    }
    
    // 括号匹配高亮开关
    const matchBracketsToggle = document.getElementById('toggle-match-brackets');
    if (matchBracketsToggle) {
      matchBracketsToggle.addEventListener('click', () => {
        this.editorSettings.matchBrackets = !this.editorSettings.matchBrackets;
        matchBracketsToggle.classList.toggle('active', this.editorSettings.matchBrackets);
        this.saveEditorSettings();
        console.log(`⚙️ 括号匹配高亮: ${this.editorSettings.matchBrackets ? '开启' : '关闭'}`);
      });
    }
  }
  
  /**
   * 更新字体大小显示
   */
  private updateFontSizeDisplay(): void {
    const display = document.getElementById('font-size-display');
    if (display) {
      display.textContent = `${this.editorSettings.fontSize}px`;
    }
    // 更新预览框字体大小
    const preview = document.getElementById('fontsize-preview-code');
    if (preview) {
      preview.style.fontSize = `${this.editorSettings.fontSize}px`;
    }
  }
  
  /**
   * 保存编辑器设置到 localStorage
   * @param triggerCallback 是否触发变更回调（行号等重启生效的设置不需要触发）
   */
  private saveEditorSettings(triggerCallback: boolean = true): void {
    try {
      localStorage.setItem('editor_settings', JSON.stringify(this.editorSettings));
      // 触发变更回调
      if (triggerCallback && this.onEditorSettingsChange) {
        this.onEditorSettingsChange(this.editorSettings);
      }
    } catch (e) {
      console.error('保存编辑器设置失败:', e);
    }
  }
  
  /**
   * 从 localStorage 加载编辑器设置
   */
  private loadEditorSettings(): void {
    try {
      const saved = localStorage.getItem('editor_settings');
      if (saved) {
        const parsed = JSON.parse(saved);
        this.editorSettings = { ...DEFAULT_EDITOR_SETTINGS, ...parsed };
      }
    } catch (e) {
      console.error('加载编辑器设置失败:', e);
    }
  }
  
  /**
   * 保存渲染器设置到 localStorage
   */
  private saveRendererSettings(): void {
    try {
      localStorage.setItem('renderer_settings', JSON.stringify(this.rendererSettings));
    } catch (e) {
      console.error('保存渲染器设置失败:', e);
    }
  }
  
  /**
   * 加载渲染器设置
   */
  private loadRendererSettings(): void {
    try {
      const saved = localStorage.getItem('renderer_settings');
      if (saved) {
        const parsed = JSON.parse(saved);
        this.rendererSettings = { ...DEFAULT_RENDERER_SETTINGS, ...parsed };
      }
    } catch (e) {
      console.error('加载渲染器设置失败:', e);
    }
  }
  
  /**
   * 保存积木器设置
   */
  private saveBlocksSettings(): void {
    try {
      localStorage.setItem('blocks_settings', JSON.stringify(this.blocksSettings));
    } catch (e) {
      console.error('保存积木器设置失败:', e);
    }
  }
  
  /**
   * 加载积木器设置
   */
  private loadBlocksSettings(): void {
    try {
      const saved = localStorage.getItem('blocks_settings');
      if (saved) {
        const parsed = JSON.parse(saved);
        this.blocksSettings = { ...DEFAULT_BLOCKS_SETTINGS, ...parsed };
      } else {
        // 如果从未保存过，初始化默认值到 localStorage
        this.blocksSettings = { ...DEFAULT_BLOCKS_SETTINGS };
        this.saveBlocksSettings();
      }
    } catch (e) {
      console.error('加载积木器设置失败:', e);
    }
  }
  
  /**
   * 获取积木器设置（对外提供）
   */
  getBlocksSettings(): BlocksSettings {
    return { ...this.blocksSettings };
  }
  
  /**
   * 设置积木器设置变更回调
   */
  setBlocksSettingsChangeCallback(callback: (settings: BlocksSettings) => void): void {
    this.onBlocksSettingsChange = callback;
  }
  
  /**
   * 渲染积木器设置视图
   */
  private renderBlocksView(): void {
    const container = document.getElementById('settings-blocks');
    if (!container) return;
    
    const { defaultStageType, executionSpeed, showGrid, showRuler } = this.blocksSettings;
    
    container.innerHTML = `
      <div class="settings-page">
        <div class="settings-content">
          <div class="settings-section">
            <h3 class="section-title">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                <path d="M20.5 11H19V7c0-1.1-.9-2-2-2h-4V3.5C13 2.12 11.88 1 10.5 1S8 2.12 8 3.5V5H4c-1.1 0-1.99.9-1.99 2v3.8H3.5c1.49 0 2.7 1.21 2.7 2.7s-1.21 2.7-2.7 2.7H2V20c0 1.1.9 2 2 2h3.8v-1.5c0-1.49 1.21-2.7 2.7-2.7 1.49 0 2.7 1.21 2.7 2.7V22H17c1.1 0 2-.9 2-2v-4h1.5c1.38 0 2.5-1.12 2.5-2.5S21.88 11 20.5 11z"/>
              </svg>
              积木器设置
            </h3>
            <p class="section-desc">配置积木编程环境的默认行为</p>
          </div>
          
          <div class="setting-cards">
            <!-- 默认舞台类型 -->
            <div class="setting-item setting-item-vertical">
              <div class="setting-info">
                <span class="setting-label">默认舞台类型</span>
                <span class="setting-desc">启动积木器时默认使用的舞台模式</span>
              </div>
              <div class="setting-control">
                <div class="stage-type-options">
                  <div class="stage-type-option ${defaultStageType === 'pen' ? 'active' : ''}" data-type="pen">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                      <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                    </svg>
                    <span>画笔</span>
                  </div>
                  <div class="stage-type-option ${defaultStageType === 'sprite' ? 'active' : ''}" data-type="sprite">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-5.5-2.5l7.51-3.49L17.5 6.5 9.99 9.99 6.5 17.5zm5.5-6.6c.61 0 1.1.49 1.1 1.1s-.49 1.1-1.1 1.1-1.1-.49-1.1-1.1.49-1.1 1.1-1.1z"/>
                    </svg>
                    <span>角色</span>
                  </div>
                  <div class="stage-type-option ${defaultStageType === 'text' ? 'active' : ''}" data-type="text">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                      <path d="M2.5 4v3h5v12h3V7h5V4h-13zm19 5h-9v3h3v7h3v-7h3V9z"/>
                    </svg>
                    <span>文字</span>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- 执行速度 -->
            <div class="setting-item setting-item-vertical">
              <div class="setting-info">
                <span class="setting-label">默认执行速度</span>
                <span class="setting-desc">积木程序运行时的默认速度</span>
              </div>
              <div class="setting-control">
                <div class="speed-options">
                  <div class="speed-option ${executionSpeed === 'slow' ? 'active' : ''}" data-speed="slow">
                    <span>慢速</span>
                  </div>
                  <div class="speed-option ${executionSpeed === 'normal' ? 'active' : ''}" data-speed="normal">
                    <span>正常</span>
                  </div>
                  <div class="speed-option ${executionSpeed === 'fast' ? 'active' : ''}" data-speed="fast">
                    <span>快速</span>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- 显示画布网格 -->
            <div class="setting-item">
              <div class="setting-info">
                <span class="setting-label">显示画布网格</span>
                <span class="setting-desc">在舞台上显示坐标网格线</span>
              </div>
              <div class="setting-control">
                <div class="setting-switch ${showGrid ? 'active' : ''}" data-setting="showGrid">
                  <div class="switch-slider"></div>
                </div>
              </div>
            </div>
            
            <!-- 显示画布尺子 -->
            <div class="setting-item">
              <div class="setting-info">
                <span class="setting-label">显示画布尺子</span>
                <span class="setting-desc">在舞台边缘显示坐标刻度尺</span>
              </div>
              <div class="setting-control">
                <div class="setting-switch ${showRuler ? 'active' : ''}" data-setting="showRuler">
                  <div class="switch-slider"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
    
    this.bindBlocksEvents();
  }
  
  /**
   * 绑定积木器设置事件
   */
  private bindBlocksEvents(): void {
    // 舞台类型选择
    const stageOptions = document.querySelectorAll('.stage-type-option');
    stageOptions.forEach(option => {
      option.addEventListener('click', () => {
        stageOptions.forEach(o => o.classList.remove('active'));
        option.classList.add('active');
        const type = option.getAttribute('data-type') as 'pen' | 'sprite' | 'text';
        
        this.blocksSettings.defaultStageType = type;
        this.saveBlocksSettings();
        this.notifyBlocksSettingsChange();
        
        const typeNames = { 'pen': '画笔', 'sprite': '角色', 'text': '文字' };
        console.log(`⚙️ 默认舞台类型: ${typeNames[type]}`);
      });
    });
    
    // 执行速度选择
    const speedOptions = document.querySelectorAll('.speed-option');
    speedOptions.forEach(option => {
      option.addEventListener('click', () => {
        speedOptions.forEach(o => o.classList.remove('active'));
        option.classList.add('active');
        const speed = option.getAttribute('data-speed') as 'slow' | 'normal' | 'fast';
        
        this.blocksSettings.executionSpeed = speed;
        this.saveBlocksSettings();
        this.notifyBlocksSettingsChange();
        
        const speedNames = { 'slow': '慢速', 'normal': '正常', 'fast': '快速' };
        console.log(`⚙️ 默认执行速度: ${speedNames[speed]}`);
      });
    });
    
    // 开关设置
    const container = document.getElementById('settings-blocks');
    container?.querySelectorAll('.setting-switch[data-setting]').forEach(sw => {
      sw.addEventListener('click', () => {
        const setting = sw.getAttribute('data-setting') as 'showGrid' | 'showRuler';
        sw.classList.toggle('active');
        
        (this.blocksSettings as any)[setting] = sw.classList.contains('active');
        this.saveBlocksSettings();
        this.notifyBlocksSettingsChange();
        
        console.log(`⚙️ ${setting}: ${sw.classList.contains('active') ? '开启' : '关闭'}`);
      });
    });
  }
  
  /**
   * 通知积木器设置变更
   */
  private notifyBlocksSettingsChange(): void {
    if (this.onBlocksSettingsChange) {
      this.onBlocksSettingsChange({ ...this.blocksSettings });
    }
  }
  
  /**
   * 应用渲染器设置到实际渲染器
   */
  applyRendererSettings(): void {
    if (!appInstance) {
      console.warn('⚙️ App 实例未就绪，延迟应用渲染器设置');
      return;
    }
    
    const { shadows, showGrid, quality, showFps } = this.rendererSettings;
    
    // 应用阴影设置
    appInstance.setShadowsEnabled(shadows);
    
    // 应用网格线设置
    appInstance.setGridVisible(showGrid);
    
    // 应用渲染质量
    appInstance.setRenderQuality(quality);
    
    // 应用帧率监控
    appInstance.setShowFps(showFps);
    
    console.log('⚙️ 渲染器设置已应用');
  }
  
  /**
   * 获取渲染器设置（对外提供）
   */
  getRendererSettings(): RendererSettings {
    return { ...this.rendererSettings };
  }
  
  /**
   * 获取当前编辑器设置（对外提供）
   */
  getEditorSettings(): EditorSettings {
    return { ...this.editorSettings };
  }
  
  /**
   * 加载通用设置
   */
  private loadGeneralSettings(): void {
    try {
      const saved = localStorage.getItem('general_settings');
      if (saved) {
        const parsed = JSON.parse(saved);
        this.generalSettings = { ...DEFAULT_GENERAL_SETTINGS, ...parsed };
      }
    } catch (e) {
      console.error('加载通用设置失败:', e);
    }
  }
  
  /**
   * 保存通用设置
   */
  private saveGeneralSettings(): void {
    try {
      localStorage.setItem('general_settings', JSON.stringify(this.generalSettings));
    } catch (e) {
      console.error('保存通用设置失败:', e);
    }
  }
  
  /**
   * 应用动画设置到全局
   */
  private applyAnimationSettings(): void {
    const { animationStyle } = this.generalSettings;
    const root = document.documentElement;
    
    // 移除所有动画风格类
    document.body.classList.remove('animation-off', 'animation-efficient', 'animation-comfort', 'animation-fresh');
    
    // 根据风格设置 CSS 变量
    switch (animationStyle) {
      case 'off':
        document.body.classList.add('animation-off');
        root.style.setProperty('--transition', 'none');
        root.style.setProperty('--touch-transition', 'none');
        break;
      case 'efficient':
        root.style.setProperty('--transition', 'all 0.1s ease-out');
        root.style.setProperty('--touch-transition', 'transform 0.08s ease-out');
        break;
      case 'comfort':
        root.style.setProperty('--transition', 'all 0.3s ease');
        root.style.setProperty('--touch-transition', 'transform 0.15s cubic-bezier(0.4, 0, 0.2, 1)');
        break;
      case 'fresh':
        root.style.setProperty('--transition', 'all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)');
        root.style.setProperty('--touch-transition', 'transform 0.2s cubic-bezier(0.68, -0.55, 0.265, 1.55)');
        break;
    }
  }
  
  /**
   * 应用主题设置到全局
   */
  private applyThemeSettings(): void {
    const { appTheme } = this.generalSettings;
    
    // 移除所有主题类
    document.body.classList.remove('theme-dark', 'theme-light');
    
    // 应用新的主题
    document.body.classList.add(`theme-${appTheme}`);
  }
  
  /**
   * 获取通用设置（对外提供）
   */
  getGeneralSettings(): GeneralSettings {
    return { ...this.generalSettings };
  }
  
  /**
   * 渲染渲染器设置视图
   */
  private renderRendererView(): void {
    const container = document.getElementById('settings-renderer');
    if (!container) return;
    
    const { antialias, shadows, showGrid, quality, showFps } = this.rendererSettings;
    
    container.innerHTML = `
      <div class="settings-page">
        <div class="settings-content">
          <div class="settings-section">
            <h3 class="section-title">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                <path d="M17.66 7.93L12 2.27 6.34 7.93c-3.12 3.12-3.12 8.19 0 11.31A7.98 7.98 0 0012 21.58a7.98 7.98 0 005.66-2.34c3.12-3.12 3.12-8.19 0-11.31zM12 19.59c-1.6 0-3.11-.62-4.24-1.76C6.62 16.69 6 15.19 6 13.59s.62-3.11 1.76-4.24L12 5.1v14.49z"/>
              </svg>
              渲染器设置
            </h3>
            <p class="section-desc">配置3D渲染器的性能与画质参数</p>
          </div>
          
          <div class="setting-cards">
            <!-- 抗锯齿 -->
            <div class="setting-item setting-item-vertical">
              <div class="setting-info">
                <span class="setting-label">抗锯齿 <span class="setting-tag">重启生效</span></span>
                <span class="setting-desc">平滑边缘，提升画面质感</span>
              </div>
              <div class="antialias-options">
                <div class="aa-option ${antialias === 'off' ? 'active' : ''}" data-aa="off">
                  <span class="aa-name">关闭</span>
                </div>
                <div class="aa-option ${antialias === 'fxaa' ? 'active' : ''}" data-aa="fxaa">
                  <span class="aa-name">FXAA</span>
                  <span class="aa-desc">快速</span>
                </div>
                <div class="aa-option ${antialias === 'msaa2x' ? 'active' : ''}" data-aa="msaa2x">
                  <span class="aa-name">2x MSAA</span>
                  <span class="aa-desc">平衡</span>
                </div>
                <div class="aa-option ${antialias === 'msaa4x' ? 'active' : ''}" data-aa="msaa4x">
                  <span class="aa-name">4x MSAA</span>
                  <span class="aa-desc">高清</span>
                </div>
              </div>
            </div>
            
            <!-- 阴影 -->
            <div class="setting-item">
              <div class="setting-info">
                <span class="setting-label">阴影效果</span>
                <span class="setting-desc">启用动态阴影，增强立体感</span>
              </div>
              <div class="setting-control">
                <div class="setting-switch ${shadows ? 'active' : ''}" data-setting="shadows">
                  <div class="switch-slider"></div>
                </div>
              </div>
            </div>
            
            <!-- 网格线 -->
            <div class="setting-item">
              <div class="setting-info">
                <span class="setting-label">显示网格线</span>
                <span class="setting-desc">显示地面网格参考线</span>
              </div>
              <div class="setting-control">
                <div class="setting-switch ${showGrid ? 'active' : ''}" data-setting="showGrid">
                  <div class="switch-slider"></div>
                </div>
              </div>
            </div>
            
            <!-- 性能模式 -->
            <div class="setting-item setting-item-vertical">
              <div class="setting-info">
                <span class="setting-label">渲染质量</span>
                <span class="setting-desc">平衡性能与画质</span>
              </div>
              <div class="quality-options">
                <div class="quality-option ${quality === 'low' ? 'active' : ''}" data-quality="low">
                  <div class="quality-icon">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                      <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 14H6v-2h6v2zm4-4H6v-2h10v2zm0-4H6V7h10v2z"/>
                    </svg>
                  </div>
                  <span class="quality-name">性能优先</span>
                </div>
                <div class="quality-option ${quality === 'medium' ? 'active' : ''}" data-quality="medium">
                  <div class="quality-icon">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                  </div>
                  <span class="quality-name">平衡模式</span>
                </div>
                <div class="quality-option ${quality === 'high' ? 'active' : ''}" data-quality="high">
                  <div class="quality-icon">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                      <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                    </svg>
                  </div>
                  <span class="quality-name">画质优先</span>
                </div>
              </div>
            </div>
            
            <!-- 帧率监控 -->
            <div class="setting-item">
              <div class="setting-info">
                <span class="setting-label">帧率监控</span>
                <span class="setting-desc">在状态栏显示实时FPS</span>
              </div>
              <div class="setting-control">
                <div class="setting-switch ${showFps ? 'active' : ''}" data-setting="showFps">
                  <div class="switch-slider"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
    
    this.bindRendererEvents();
  }
  
  /**
   * 绑定渲染器设置事件
   */
  private bindRendererEvents(): void {
    // 开关类设置（阴影、网格线、FPS）
    const switches = document.querySelectorAll('#settings-renderer .setting-switch');
    switches.forEach(switchEl => {
      switchEl.addEventListener('click', () => {
        switchEl.classList.toggle('active');
        const setting = switchEl.getAttribute('data-setting') as 'shadows' | 'showGrid' | 'showFps';
        const isActive = switchEl.classList.contains('active');
        
        // 更新设置
        (this.rendererSettings as any)[setting] = isActive;
        
        // 应用到渲染器
        if (appInstance) {
          switch (setting) {
            case 'shadows':
              appInstance.setShadowsEnabled(isActive);
              break;
            case 'showGrid':
              appInstance.setGridVisible(isActive);
              break;
            case 'showFps':
              appInstance.setShowFps(isActive);
              break;
          }
        }
        
        this.saveRendererSettings();
        console.log(`⚙️ 渲染器设置: ${setting} = ${isActive}`);
      });
    });
    
    // 抗锯齿选项
    const aaOptions = document.querySelectorAll('.aa-option');
    aaOptions.forEach(option => {
      option.addEventListener('click', () => {
        aaOptions.forEach(o => o.classList.remove('active'));
        option.classList.add('active');
        const aa = option.getAttribute('data-aa') as 'off' | 'fxaa' | 'msaa2x' | 'msaa4x';
        
        this.rendererSettings.antialias = aa;
        this.saveRendererSettings();
        console.log(`⚙️ 抗锯齿: ${aa} (重启后生效)`);
      });
    });
    
    // 质量选项
    const qualityOptions = document.querySelectorAll('.quality-option');
    qualityOptions.forEach(option => {
      option.addEventListener('click', () => {
        qualityOptions.forEach(o => o.classList.remove('active'));
        option.classList.add('active');
        const quality = option.getAttribute('data-quality') as 'low' | 'medium' | 'high';
        
        // 更新设置
        this.rendererSettings.quality = quality;
        
        // 应用到渲染器
        if (appInstance) {
          appInstance.setRenderQuality(quality);
        }
        
        this.saveRendererSettings();
        console.log(`⚙️ 渲染质量: ${quality}`);
      });
    });
  }
}

// 导出单例
export const settingsModule = new SettingsModule();
